import { useState } from "react";
import { normalizeGeneratedText } from "@/lib/normalize-generated-text";
import type {
    QuizOption,
    QuizQuestion,
    QuizReviewContentProps,
} from "@/components/admin/review/types";

export default function QuizReviewContent({ item, isEditing, onUpdate }: QuizReviewContentProps) {
    const transformQuestions = (questions: QuizQuestion[] | undefined) => {
        if (!questions) return [];
        return questions.map(q => {
            let options = q.options;
            // Robust transformation for options
            if (Array.isArray(options)) {
                options = options.map((opt: unknown) => {
                    if (typeof opt === 'string') {
                        return {
                            text: normalizeGeneratedText(opt),
                            isCorrect: normalizeGeneratedText(opt) === normalizeGeneratedText(String(q.correct_answer ?? ""))
                        };
                    }
                    // If it's already an object, ensure it has the expected 'text' property
                    if (typeof opt === 'object' && opt !== null) {
                        const optObj = opt as Record<string, unknown>;
                        return {
                            ...optObj,
                            text: normalizeGeneratedText((optObj.text as string) || (optObj.text_content as string) || (optObj.label as string) || ""),
                            isCorrect:
                                (optObj.isCorrect as boolean) ??
                                (normalizeGeneratedText(String(optObj.text ?? "")) === normalizeGeneratedText(String(q.correct_answer ?? "")))
                        };
                    }
                    return { text: normalizeGeneratedText(String(opt)), isCorrect: false };
                });
            }
            return {
                ...q,
                text: typeof q.text === "string" ? normalizeGeneratedText(q.text) : q.text,
                question_text: typeof q.question_text === "string" ? normalizeGeneratedText(q.question_text) : q.question_text,
                explanation: typeof q.explanation === "string" ? normalizeGeneratedText(q.explanation) : q.explanation,
                lesson_title: typeof q.lesson_title === "string" ? normalizeGeneratedText(q.lesson_title) : q.lesson_title,
                options
            };
        });
    };

    const [formData, setFormData] = useState({
        title: item.title || "",
        description: typeof item.description === "string" ? normalizeGeneratedText(item.description) : "",
        questions: transformQuestions(item.placement_questions || []),
    });


    const handleMetadataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const newData = { ...formData, [name]: value };
        setFormData(newData);
        onUpdate(newData);
    };

    const handleQuestionChange = (index: number, field: string, value: string) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
        const newData = { ...formData, questions: updatedQuestions };
        setFormData(newData);
        onUpdate(newData);
    };

    const handleOptionChange = (qIndex: number, oIndex: number, value: string) => {
        const updatedQuestions = [...formData.questions];
        const updatedOptions = [...updatedQuestions[qIndex].options];
        updatedOptions[oIndex] = { ...updatedOptions[oIndex], text: value };
        updatedQuestions[qIndex] = { ...updatedQuestions[qIndex], options: updatedOptions };

        const newData = { ...formData, questions: updatedQuestions };
        setFormData(newData);
        onUpdate(newData);
    };

    return (
        <div className="space-y-6">
            {/* Quiz Metadata */}
            <div className="bg-card shadow sm:rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-border bg-muted">
                    <h3 className="text-base font-semibold leading-6 text-foreground">
                        Quiz Details
                    </h3>
                </div>
                <div className="px-4 py-4">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-base font-medium text-foreground mb-2">Title</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleMetadataChange}
                                    className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-base py-2.5 px-3 bg-background text-foreground"
                                />
                            ) : (
                                <p className="mt-1 text-base font-medium text-foreground">{formData.title}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-base font-medium text-foreground mb-2">Description</label>
                            {isEditing ? (
                                <textarea
                                    name="description"
                                    rows={3}
                                    value={formData.description}
                                    onChange={handleMetadataChange}
                                    className="mt-1 block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-base p-3 bg-background text-foreground"
                                />
                            ) : (
                                <p className="mt-1 text-base text-muted-foreground">{formData.description}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Questions */}
            <div className="space-y-8">
                {(() => {
                    // Group questions by level_name
                    const groups: Record<string, { questions: QuizQuestion[], originalIndices: number[] }> = {};

                    formData.questions.forEach((q, idx) => {
                        const level = (q.level_name as string) || "Other";
                        if (!groups[level]) {
                            groups[level] = { questions: [], originalIndices: [] };
                        }
                        groups[level].questions.push(q);
                        groups[level].originalIndices.push(idx);
                    });

                    // Pedagogical order for levels
                    const levelOrder = ["Associate", "Intermediate", "Advanced"];

                    const sortedLevels = Object.keys(groups).sort((a, b) => {
                        const indexA = levelOrder.indexOf(a);
                        const indexB = levelOrder.indexOf(b);

                        // If both are in our list, use that order
                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        // If only one is in the list, the known level comes first
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;

                        // Default to numeric/alphabetical
                        const numA = parseInt(a.match(/\d+/)?.[0] || "0");
                        const numB = parseInt(b.match(/\d+/)?.[0] || "0");
                        if (numA !== numB) return numA - numB;
                        return a.localeCompare(b);
                    });

                    return sortedLevels.map((level) => (
                        <div key={level} className="space-y-4">
                            <div className="flex items-center gap-3 pb-2 border-b border-border">
                                <h3 className="text-xl font-bold text-foreground">{level}</h3>
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted border border-border text-muted-foreground">
                                    {groups[level].questions.length} questions
                                </span>
                            </div>

                            <div className="grid gap-4">
                                {groups[level].questions.map((question, groupIdx) => {
                                    const originalIdx = groups[level].originalIndices[groupIdx];
                                    return (
                                        <div key={originalIdx} className="bg-card shadow sm:rounded-lg overflow-hidden border border-border">
                                            <div className="px-4 py-3 border-b border-border bg-muted/50 flex items-center justify-between">
                                                <h4 className="text-sm font-bold leading-6 text-foreground/80">
                                                    Question {groupIdx + 1}
                                                </h4>
                                                <div className="flex items-center gap-2">
                                                    {question.lesson_title && (
                                                        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                                                            {question.lesson_title as string}
                                                        </span>
                                                    )}
                                                    <span className="inline-flex items-center rounded-full bg-background px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-foreground border border-border">
                                                        {question.difficulty}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="px-4 py-5 sm:p-6">
                                                {/* Question Text */}
                                                <div className="mb-4">
                                                    <label className="block text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Question Text</label>
                                                    {isEditing ? (
                                                        <textarea
                                                            rows={2}
                                                            value={question.text || question.question_text}
                                                            onChange={(e) => handleQuestionChange(originalIdx, 'text', e.target.value)}
                                                            className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-base p-3 bg-background text-foreground"
                                                        />
                                                    ) : (
                                                        <div className="text-base text-foreground font-semibold">
                                                            {question.text || question.question_text}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Options */}
                                                {question.options && (
                                                    <div className="space-y-2">
                                                        <label className="block text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Options</label>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            {question.options.map((option: QuizOption, oIdx: number) => (
                                                                <div
                                                                    key={oIdx}
                                                                    className={`p-3 rounded-xl border-2 transition-all ${option.isCorrect
                                                                        ? "border-emerald-500/50 bg-emerald-50/30"
                                                                        : "border-slate-100 bg-slate-50/50"
                                                                        }`}
                                                                >
                                                                    <div className="flex items-start gap-3">
                                                                        <div className={`mt-1 h-3 w-3 rounded-full flex-shrink-0 ${option.isCorrect ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" : "bg-slate-300"
                                                                            }`} />
                                                                        {isEditing ? (
                                                                            <input
                                                                                type="text"
                                                                                value={option.text}
                                                                                onChange={(e) => handleOptionChange(originalIdx, oIdx, e.target.value)}
                                                                                className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-sm py-1.5 px-3 bg-background text-foreground"
                                                                            />
                                                                        ) : (
                                                                            <span className={`text-sm ${option.isCorrect ? "font-bold text-slate-800" : "text-slate-600"}`}>
                                                                                {option.text}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Explanation */}
                                                {(isEditing || question.explanation) && (
                                                    <div className="mt-6 pt-4 border-t border-slate-100">
                                                        <label className="block text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Explanation</label>
                                                        {isEditing ? (
                                                            <textarea
                                                                rows={2}
                                                                value={question.explanation || ""}
                                                                onChange={(e) => handleQuestionChange(originalIdx, 'explanation', e.target.value)}
                                                                className="block w-full rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-sm p-3 bg-background text-foreground"
                                                                placeholder="Add an explanation for the correct answer..."
                                                            />
                                                        ) : (
                                                            <div className="rounded-xl bg-indigo-50/50 px-4 py-3 text-sm text-indigo-900 border border-indigo-100/50 italic">
                                                                <span className="font-bold mr-1">Explanation:</span>
                                                                {question.explanation}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ));
                })()}
            </div>
        </div>
    );
}
